/*
 * Warns at checkout when the cart holds more than one subscription.
 *
 * Subscriptions run through Recharge, which starts a separate subscription for
 * every subscription line item, so a cart with (say) a 30 day trial plus an
 * annual plan bills the customer twice. Nothing in the cart stops that, so this
 * intercepts the checkout button and explains what is about to happen. The
 * customer can still go ahead if two subscriptions is genuinely what they want.
 *
 * Checkout happens in the Recharge cart drawer, which matters twice over:
 *   - the drawer lives in a shadow DOM, so event.target is retargeted to the
 *     host element and closest() never sees the real button. composedPath()
 *     walks the true click path instead.
 *   - the click must be stopped SYNCHRONOUSLY. Awaiting /cart.js first lets the
 *     event finish dispatching, and Recharge navigates away before the answer
 *     arrives. So every checkout click is halted first, then replayed through
 *     Recharge's own handler once we know the cart is fine.
 */
if (!customElements.get('ssg-subscription-warning')) {
  customElements.define(
    'ssg-subscription-warning',
    class SsgSubscriptionWarning extends HTMLElement {
      constructor() {
        super();

        this.handles = (this.dataset.handles || '')
          .split(/[\n,]/)
          .map((h) => h.trim().toLowerCase())
          .filter(Boolean);

        // Set while replaying a click we already approved, so it passes through.
        this.passThrough = false;
        this.lastTrigger = null;
        this.hiddenDrawer = null;

        this.close = this.close.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onCheckoutClick = this.onCheckoutClick.bind(this);

        this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', this.close));

        const continueButton = this.querySelector('[data-continue]');
        if (continueButton) {
          continueButton.addEventListener('click', () => {
            this.close();
            this.replayCheckout();
          });
        }

        const editButton = this.querySelector('[data-edit]');
        if (editButton) {
          editButton.addEventListener('click', () => {
            const hadDrawer = this.hiddenDrawer != null;
            this.close();
            // With the Recharge drawer restored they are already looking at the
            // cart; only send them to /cart when there is no drawer to go back to.
            if (!hadDrawer && !window.location.pathname.startsWith('/cart')) {
              window.location.href = '/cart';
            }
          });
        }

        document.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onCheckoutClick, true);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onCheckoutClick, true);
      }

      isCheckoutTrigger(event) {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

        for (const node of path) {
          if (!node || node.nodeType !== 1) continue;

          if (node.matches?.('button[name="checkout"], input[name="checkout"]')) return node;

          const href = node.getAttribute?.('href') || '';
          if (href.includes('/checkout') || href.includes('checkout.rechargeapps.com')) return node;

          // Recharge's drawer button carries no name attribute, so match its label.
          const tag = node.tagName;
          if (tag === 'BUTTON' || tag === 'A') {
            const label = (node.textContent || '').trim().toLowerCase();
            if (/^check\s?out\b/.test(label)) return node;
          }
        }

        return null;
      }

      onCheckoutClick(event) {
        if (this.passThrough) return;

        const trigger = this.isCheckoutTrigger(event);
        if (!trigger) return;

        // Stop it now, before any await. Whether checkout proceeds is decided
        // in checkCart(), which either replays this click or opens the warning.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.lastTrigger = trigger;
        this.checkCart();
      }

      async checkCart() {
        const cart = await this.fetchCart();

        // Never strand a customer because a lookup failed.
        if (!cart) {
          this.replayCheckout();
          return;
        }

        const subscriptions = this.subscriptionItems(cart);
        if (subscriptions.length < 2) {
          this.replayCheckout();
          return;
        }

        this.renderItems(subscriptions);
        this.open();
      }

      /*
       * Re-fires the original click with the guard down, so Recharge's own
       * handler runs and builds the checkout URL the way it normally would.
       */
      replayCheckout() {
        const trigger = this.lastTrigger;
        this.passThrough = true;

        if (trigger && document.contains(trigger.getRootNode()?.host || trigger)) {
          trigger.click();
        } else {
          window.location.href = '/checkout';
        }

        // Drop the guard once the replayed click has been handled.
        setTimeout(() => {
          this.passThrough = false;
        }, 2000);
      }

      async fetchCart() {
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
          return await response.json();
        } catch (e) {
          console.error('Subscription warning: could not read the cart', e);
          return null;
        }
      }

      subscriptionItems(cart) {
        const seen = new Set();
        const items = [];

        (cart.items || []).forEach((item) => {
          const handle = (item.handle || item.product_handle || '').toLowerCase();
          const isSubscription =
            item.selling_plan_allocation != null || this.handles.some((h) => handle === h);
          if (!isSubscription || seen.has(handle)) return;
          seen.add(handle);
          items.push(item);
        });

        return items;
      }

      renderItems(items) {
        const list = this.querySelector('[data-items]');
        if (!list) return;

        list.innerHTML = '';
        items.forEach((item) => {
          const li = document.createElement('li');
          li.textContent = item.product_title || item.title;
          const variant = item.variant_title;
          if (variant && variant !== 'Default Title') {
            const span = document.createElement('span');
            span.textContent = ` (${variant})`;
            li.appendChild(span);
          }
          list.appendChild(li);
        });
      }

      /*
       * The Recharge drawer is fixed-position inside its own stacking context,
       * so no z-index of ours reliably clears it. Hide the host while the
       * warning is up and restore it afterwards.
       */
      hideRechargeDrawer() {
        const host = document.getElementById('recharge-storefront-experiences__cart-drawer');
        if (!host) return;
        this.hiddenDrawer = { host, display: host.style.display };
        host.style.setProperty('display', 'none', 'important');
      }

      restoreRechargeDrawer() {
        if (!this.hiddenDrawer) return;
        const { host, display } = this.hiddenDrawer;
        if (display) {
          host.style.setProperty('display', display);
        } else {
          host.style.removeProperty('display');
        }
        this.hiddenDrawer = null;
      }

      open() {
        this.hideRechargeDrawer();
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        document.body.classList.add('ssg-subwarn-open');
        const firstButton = this.querySelector('[data-edit], [data-continue]');
        if (firstButton) firstButton.focus();
      }

      close() {
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        document.body.classList.remove('ssg-subwarn-open');
        this.restoreRechargeDrawer();
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }
    }
  );
}
